/**
 * One exercise inside a plan being written by hand, and the fields that
 * describe its target.
 *
 * Shared by the plan builder and the one-off planner rather than copied. The
 * inputs shown here follow the exercise's tracking mode, and that rule already
 * exists in three places (the log screen, the server's validation, the plan
 * generator's prompt) — a fourth and fifth copy diverging is how a plank ends
 * up with a rep range in one screen and a duration in another.
 */

import { measuresOf, type Exercise } from '../db'
import { unitFor, useDistanceOverrides } from '../lib/distance'
import { maskDuration, normalizeDuration, type DistancePref, type Unit } from '../lib/units'

export type DraftItem = {
  key: string
  exercise: Exercise
  sets: string
  reps: string
  duration: string
  distance: string
  rpe: string
  rest: string
  notes: string
}


let seq = 0
export const nextKey = () => `k${seq++}`

export function newItem(exercise: Exercise): DraftItem {
  const ms = measuresOf(exercise)
  return {
    key: nextKey(),
    exercise,
    sets: '3',
    // Defaults only where they're meaningful. A blank duration on a plank is
    // an honest "you haven't said yet"; prefilling 45 would put a number in
    // the plan that nobody chose.
    reps: ms.includes('reps') ? '8' : '',
    duration: '',
    distance: '',
    rpe: '',
    rest: '120',
    notes: '',
  }
}

export function ItemRow({
  item,
  unit,
  distancePref,
  onChange,
  onRemove,
}: {
  item: DraftItem
  unit: Unit
  distancePref: DistancePref
  onChange: (patch: Partial<DraftItem>) => void
  onRemove: () => void
}) {
  const ms = measuresOf(item.exercise)
  const overrides = useDistanceOverrides()
  const du = unitFor(item.exercise, { unit, pref: distancePref, overrides })
  const small = { marginTop: 0, fontSize: '.9rem' }

  return (
    <div
      style={{
        marginTop: '.5rem',
        padding: '.5rem .6rem',
        background: 'var(--surface)',
        borderRadius: 'var(--radius)',
      }}
    >
      <div className="row">
        <span style={{ display: 'flex', alignItems: 'center', gap: '.5rem', minWidth: 0 }}>
          {/* The grip. Rows here are mostly text inputs, where a long press means
              "put the cursor there" — so the drag has to come from something with
              no other job. touch-action:none from the outset, not switched on at
              grab time: the browser latches that value when the gesture starts,
              so setting it later would be too late to stop the page panning. */}
          <span
            data-drag-handle
            aria-label={`Reorder ${item.exercise.name}`}
            title="Drag to reorder"
            style={{
              cursor: 'grab',
              touchAction: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              color: 'var(--muted)',
              // Thumb-sized: a 12px glyph is not a drag handle on a phone.
              padding: '.35rem .5rem',
              margin: '-.35rem -.25rem -.35rem -.5rem',
              lineHeight: 1,
              fontSize: '1.05rem',
              flex: '0 0 auto',
            }}
          >
            ≡
          </span>
          <strong style={{ fontSize: '.92rem' }}>{item.exercise.name}</strong>
        </span>
        <button
          className="secondary"
          style={{ marginTop: 0, padding: '.2rem .5rem', minHeight: 0, fontSize: '.8rem' }}
          onClick={onRemove}
        >
          Remove
        </button>
      </div>

      <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', marginTop: '.3rem' }}>
        <label style={{ flex: '1 1 4rem' }}>
          <span className="muted" style={{ fontSize: '.78rem' }}>Sets</span>
          <input
            type="number"
            inputMode="numeric"
            value={item.sets}
            style={small}
            onChange={(e) => onChange({ sets: e.target.value })}
          />
        </label>

        {ms.includes('reps') && (
          <label style={{ flex: '1 1 4rem' }}>
            <span className="muted" style={{ fontSize: '.78rem' }}>Reps</span>
            <input
              type="number"
              inputMode="numeric"
              value={item.reps}
              style={small}
              onChange={(e) => onChange({ reps: e.target.value })}
            />
          </label>
        )}

        {ms.includes('distance') && (
          <label style={{ flex: '1 1 5rem' }}>
            <span className="muted" style={{ fontSize: '.78rem' }}>
              Distance ({du})
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              value={item.distance}
              style={small}
              onChange={(e) => onChange({ distance: e.target.value })}
            />
          </label>
        )}

        {ms.includes('time') && (
          <label style={{ flex: '1 1 5rem' }}>
            <span className="muted" style={{ fontSize: '.78rem' }}>Time</span>
            {/* Clock masking, as on the log form: 1-3-0 types 1:30 on a
                keypad that has no colon. */}
            <input
              type="text"
              inputMode="numeric"
              placeholder="45 or 1:30"
              value={item.duration}
              style={small}
              onChange={(e) => onChange({ duration: maskDuration(e.target.value) })}
              onBlur={() => onChange({ duration: normalizeDuration(item.duration) })}
            />
          </label>
        )}

        <label style={{ flex: '1 1 4rem' }}>
          <span className="muted" style={{ fontSize: '.78rem' }}>RPE</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            placeholder="—"
            value={item.rpe}
            style={small}
            onChange={(e) => onChange({ rpe: e.target.value })}
          />
        </label>

        <label style={{ flex: '1 1 4rem' }}>
          <span className="muted" style={{ fontSize: '.78rem' }}>Rest (s)</span>
          <input
            type="number"
            inputMode="numeric"
            value={item.rest}
            style={small}
            onChange={(e) => onChange({ rest: e.target.value })}
          />
        </label>
      </div>

      {ms.includes('distance') && ms.includes('time') && (
        <div className="muted" style={{ fontSize: '.78rem', marginTop: '.3rem' }}>
          Distance or time — either is a complete target, both is a pace goal.
        </div>
      )}

      {/* The cue you'd otherwise forget by the time you're under the bar.
          Shown again in the queue while you're actually doing the exercise,
          which is the only moment it's worth anything. */}
      <input
        type="text"
        placeholder="Note — pause at the bottom, tempo 3-1-1…"
        value={item.notes}
        style={{ marginTop: '.35rem', fontSize: '.85rem' }}
        onChange={(e) => onChange({ notes: e.target.value })}
      />
    </div>
  )
}
